import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { countWordsCapped } from '../utils/countWords.ts';

import { useFlagToShare } from './useFlagToShare.ts';

const WORD_COUNT_DEBOUNCE_MS = 500;

export type HandleLiveDocsDocChange = (editorView: EditorView) => void;

export type UseLiveDocsFlagToShareParams = {
	contentId: string;
	isLivePage: boolean;
};

type LiveDocsEditorApi = Pick<FullPageEditorPresetAPI, 'core'>;

type LiveDocsSession = {
	contentId: string;
};

type PendingRecompute = {
	editorView: EditorView;
	session: LiveDocsSession;
};

/** Produces Live Docs word-delta progress for the shared Flag-to-Share orchestrator. */
export const useLiveDocsFlagToShare = (
	editorApi: LiveDocsEditorApi | null | undefined,
	{ contentId, isLivePage }: UseLiveDocsFlagToShareParams,
): HandleLiveDocsDocChange | null => {
	const { shouldCollectProgress, notifyProgress } = useFlagToShare({
		type: 'live-doc',
		contentId,
	});
	const enabled =
		isLivePage && shouldCollectProgress && Boolean(editorApi?.core?.actions?.requestDocument);

	const baselineWordCountRef = useRef<number | null>(null);
	const notifyProgressRef = useRef(notifyProgress);
	const pendingRecomputeRef = useRef<PendingRecompute | null>(null);
	const sessionRef = useRef<LiveDocsSession | null>(null);

	useLayoutEffect(() => {
		notifyProgressRef.current = notifyProgress;
	}, [notifyProgress]);

	useEffect(() => {
		baselineWordCountRef.current = null;
		pendingRecomputeRef.current = null;
		notifyProgressRef.current(null);
	}, [contentId, editorApi, isLivePage]);

	useEffect(() => {
		if (!enabled || !editorApi?.core?.actions?.requestDocument) {
			return;
		}

		const session = { contentId };
		sessionRef.current = session;

		return () => {
			if (sessionRef.current === session) {
				sessionRef.current = null;
			}
			if (pendingRecomputeRef.current?.session === session) {
				pendingRecomputeRef.current = null;
			}
		};
	}, [contentId, editorApi, enabled]);

	const recomputeWordDelta = useCallback((session: LiveDocsSession, editorView: EditorView) => {
		if (sessionRef.current !== session) {
			return;
		}

		if (baselineWordCountRef.current === null) {
			pendingRecomputeRef.current = { editorView, session };
			return;
		}

		const currentCount = countWordsCapped(editorView.state.doc.toJSON());
		notifyProgressRef.current({
			type: 'live-doc',
			wordDelta: Math.max(currentCount - baselineWordCountRef.current, 0),
		});
	}, []);

	useEffect(() => {
		if (!enabled || !editorApi?.core?.actions?.requestDocument) {
			return;
		}

		const session = sessionRef.current;
		if (!session || session.contentId !== contentId) {
			return;
		}

		editorApi.core.actions.requestDocument(
			(doc) => {
				if (sessionRef.current !== session || !doc || baselineWordCountRef.current !== null) {
					return;
				}

				baselineWordCountRef.current = countWordsCapped(doc);
				const pendingRecompute = pendingRecomputeRef.current;
				if (pendingRecompute?.session === session) {
					pendingRecomputeRef.current = null;
					recomputeWordDelta(session, pendingRecompute.editorView);
				}
			},
			{ alwaysFire: true },
		);
	}, [contentId, editorApi, enabled, recomputeWordDelta]);

	const debouncedRecompute = useMemo(
		() => debounce(recomputeWordDelta, WORD_COUNT_DEBOUNCE_MS),
		[recomputeWordDelta],
	);

	const handleDocChange = useCallback<HandleLiveDocsDocChange>(
		(editorView) => {
			const session = sessionRef.current;
			if (session) {
				debouncedRecompute(session, editorView);
			}
		},
		[debouncedRecompute],
	);

	useEffect(() => {
		if (!enabled) {
			debouncedRecompute.cancel();
		}

		return () => debouncedRecompute.cancel();
	}, [debouncedRecompute, enabled]);

	return enabled ? handleDocChange : null;
};
